import { useState } from 'react'
import type { Tenant, TenantSecrets } from '../../services/api'

interface TenantFormProps {
  tenant: Tenant | null
  onSave: (data: Partial<Tenant> & { secrets?: TenantSecrets }) => void
  onClose: () => void
}

export default function TenantForm({ tenant, onSave, onClose }: TenantFormProps) {
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    slug: tenant?.slug || '',
    status: tenant?.status || 'trial',
    plan: tenant?.plan || 'standard',
    crowdstrike_client_id: tenant?.crowdstrike_client_id || '',
    crowdstrike_client_secret: '',
    crowdstrike_region: tenant?.crowdstrike_region || 'eu-1',
    crowdstrike_tag: tenant?.crowdstrike_tag || '',
    jumpcloud_org_id: tenant?.jumpcloud_org_id || '',
    primary_color: tenant?.primary_color || '#FF9B71',
    domain: (tenant as any)?.domain || '',
    logo_url: (tenant as any)?.logo_url || '',
    socradar_enabled: !!(tenant as any)?.socradar_enabled,
    socradar_company_id: (tenant as any)?.socradar_company_id || ''
  })

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target
    const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value
    const name = target.name
    setFormData(prev => ({ ...prev, [name]: value }))

    // Auto-generate slug from name
    if (name === 'name' && !tenant && typeof value === 'string') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setFormData(prev => ({ ...prev, slug }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaving(true)

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('El nombre es requerido')
      }
      if (!formData.slug.trim()) {
        throw new Error('El slug es requerido')
      }
      if (!/^[a-z0-9-]+$/.test(formData.slug)) {
        throw new Error('El slug solo puede contener letras minusculas, numeros y guiones')
      }

      // Prepare data
      const tenantData: Partial<Tenant> & { secrets?: TenantSecrets } = {
        name: formData.name,
        slug: formData.slug,
        status: formData.status as Tenant['status'],
        plan: formData.plan as Tenant['plan'],
        crowdstrike_client_id: formData.crowdstrike_client_id || undefined,
        crowdstrike_region: formData.crowdstrike_region,
        crowdstrike_tag: formData.crowdstrike_tag || undefined,
        jumpcloud_org_id: formData.jumpcloud_org_id || undefined,
        primary_color: formData.primary_color,
        domain: formData.domain || undefined,
        logo_url: formData.logo_url || undefined,
        socradar_enabled: formData.socradar_enabled,
        socradar_company_id: formData.socradar_company_id || undefined,
      } as any

      // Add secrets only if provided (JumpCloud API key is now global, not per-tenant)
      if (formData.crowdstrike_client_secret) {
        tenantData.secrets = {
          crowdstrike_client_secret: formData.crowdstrike_client_secret
        }
      }

      await onSave(tenantData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {tenant ? 'Editar Cliente' : 'Agregar Nuevo Cliente'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6" noValidate>
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Informacion Basica</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                  placeholder="Ej: Mi Empresa"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Slug (URL) *
                </label>
                <div className="flex items-center">
                  <span className="text-gray-500 text-sm mr-2">https://</span>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                    placeholder="mi-empresa"
                    pattern="[a-z0-9-]+"
                    required
                  />
                  <span className="text-gray-500 text-sm ml-2">.jarix-dashboard.pages.dev</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Estado
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                >
                  <option value="trial">Trial</option>
                  <option value="active">Activo</option>
                  <option value="suspended">Suspendido</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Plan
                </label>
                <select
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                >
                  <option value="free">Essentials ($20/usuario)</option>
                  <option value="standard">Shield ($40/usuario)</option>
                  <option value="enterprise">Advance ($60/usuario)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Color Primario
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleChange}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Dominio
                </label>
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                  placeholder="ejemplo.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se usa para generar el logo automaticamente (Clearbit)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Logo URL (override)
                </label>
                <input
                  type="text"
                  name="logo_url"
                  value={formData.logo_url}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  URL directa al logo. Si esta vacio, se usa el dominio
                </p>
              </div>
            </div>
          </div>

          {/* CrowdStrike Config */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-sm">CrowdStrike</span>
              Configuracion
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Client ID
                </label>
                <input
                  type="text"
                  name="crowdstrike_client_id"
                  value={formData.crowdstrike_client_id}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange font-mono text-sm"
                  placeholder="1ff0e9495a564193bead913573b73b79"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Client Secret {tenant && '(dejar vacio para mantener)'}
                </label>
                <input
                  type="password"
                  name="crowdstrike_client_secret"
                  value={formData.crowdstrike_client_secret}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange font-mono text-sm"
                  placeholder="••••••••••••••••••••"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Region API
                </label>
                <select
                  name="crowdstrike_region"
                  value={formData.crowdstrike_region}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                >
                  <option value="eu-1">Europa (EU-1)</option>
                  <option value="us-1">US (US-1)</option>
                  <option value="us-2">US (US-2)</option>
                  <option value="us-gov-1">US Gov (US-GOV-1)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tag de Dispositivos (FalconGroupingTags)
                </label>
                <input
                  type="text"
                  name="crowdstrike_tag"
                  value={formData.crowdstrike_tag}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange font-mono text-sm"
                  placeholder="Ej: Empresa_devices"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tag asignado en CrowdStrike para filtrar los dispositivos de este cliente
                </p>
              </div>
            </div>
          </div>

          {/* JumpCloud Config */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-sm">JumpCloud</span>
              Configuracion
            </h3>
            <p className="text-sm text-gray-400">
              Se usa el API Key multi-tenant global. Solo necesitas el Organization ID del cliente.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Organization ID *
              </label>
              <input
                type="text"
                name="jumpcloud_org_id"
                value={formData.jumpcloud_org_id}
                onChange={handleChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange font-mono text-sm"
                placeholder="646f7bb4a6308a8bd35fff0f"
              />
              <p className="text-xs text-gray-500 mt-1">
                ID unico de la organizacion del cliente en JumpCloud
              </p>
            </div>
          </div>

          {/* SocRadar Config */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-sm">SocRadar</span>
              Configuracion
            </h3>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="socradar_enabled"
                checked={formData.socradar_enabled}
                onChange={handleChange}
                className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-jarix-orange focus:ring-jarix-orange"
              />
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  SocRadar Habilitado
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Habilita acceso directo a Brand Protection, Digital Footprint y Dark Web para este tenant.
                  Si esta deshabilitado, se muestra el Informe de Ciber-Riesgo general.
                </p>
              </div>
            </div>

            {formData.socradar_enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company ID
                </label>
                <input
                  type="text"
                  name="socradar_company_id"
                  value={formData.socradar_company_id}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange font-mono text-sm"
                  placeholder="59009"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ID de la compania en SocRadar (solo si esta habilitado)
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-jarix-orange hover:bg-jarix-pink disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {tenant ? 'Guardar Cambios' : 'Crear Cliente'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
